import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarBlank, PaperPlaneTilt, CurrencyInr, ArrowClockwise, Scales, DownloadSimple, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const dmy = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("-") : "");
const fmtDate = (d) => {
    if (!d) return "—";
    try {
        const dt = new Date(d + "T00:00:00");
        const p = (n) => String(n).padStart(2, "0");
        const wk = dt.toLocaleDateString("en-IN", { weekday: "short" });
        return `${wk} ${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
    } catch {
        return d;
    }
};

// Default range: current month
const firstOfMonth = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

const csvCell = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function PaymentCalendar() {
    const [dateFrom, setDateFrom] = useState(firstOfMonth());
    const [dateTo, setDateTo] = useState(today());
    const [partyId, setPartyId] = useState("all");
    const [kind, setKind] = useState("payout"); // payout | payment | both
    const [mode, setMode] = useState("all");
    const [parties, setParties] = useState([]);
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [markRow, setMarkRow] = useState(null); // { id, note }
    const [marking, setMarking] = useState(false);

    useEffect(() => {
        api.get("/parties").then((r) => setParties(r.data || [])).catch(() => setParties([]));
    }, []);

    const load = async () => {
        setBusy(true);
        try {
            const params = { kind };
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;
            if (partyId && partyId !== "all") params.party_id = partyId;
            if (mode && mode !== "all") params.mode = mode;
            const { data } = await api.get("/reports/payment-calendar", { params });
            setData(data);
        } catch (e) {
            toast.error("Failed to load payment calendar");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const groups = data?.groups || [];
    const availableModes = data?.available_modes || [];
    const kindLabel = useMemo(() => (
        kind === "payout" ? "Payments made to parties"
            : kind === "payment" ? "Payments received from parties"
                : "All payments (in & out)"
    ), [kind]);

    // Flat list of every row across all day groups (respects current filters)
    const allRows = useMemo(() => groups.flatMap((g) => g.rows.map((r) => ({ ...r, date: g.date }))), [groups]);

    // Reconcile summary computed client-side from the loaded rows
    const summary = useMemo(() => {
        const byMode = {};
        const byParty = {};
        const unreconciled = [];
        let paidOut = 0, received = 0;
        for (const r of allRows) {
            if (r.txn_type === "payout") paidOut += r.amount; else received += r.amount;
            const m = r.payment_mode || "(no mode)";
            byMode[m] = (byMode[m] || 0) + (r.txn_type === "payout" ? r.amount : r.amount);
            const pn = r.party_name || "(unknown)";
            if (!byParty[pn]) byParty[pn] = { payout: 0, payment: 0 };
            byParty[pn][r.txn_type === "payout" ? "payout" : "payment"] += r.amount;
            if (!r.reconciled) unreconciled.push(r);
        }
        return {
            paidOut, received, net: received - paidOut,
            byMode: Object.entries(byMode).sort((a, b) => b[1] - a[1]),
            byParty: Object.entries(byParty).sort((a, b) => (b[1].payout + b[1].payment) - (a[1].payout + a[1].payment)),
            unreconciled,
        };
    }, [allRows]);

    const exportCsv = () => {
        const lines = [];
        lines.push(["Payment Calendar — Reconciliation Summary"].map(csvCell).join(","));
        lines.push([`Period: ${data?.date_from || "—"} to ${data?.date_to || "—"}`].map(csvCell).join(","));
        lines.push("");
        lines.push(["Total Paid Out", summary.paidOut].map(csvCell).join(","));
        lines.push(["Total Received", summary.received].map(csvCell).join(","));
        lines.push(["Net Position", summary.net].map(csvCell).join(","));
        lines.push("");
        lines.push(["Breakdown by Mode", "Amount"].map(csvCell).join(","));
        summary.byMode.forEach(([m, amt]) => lines.push([m, amt].map(csvCell).join(",")));
        lines.push("");
        lines.push(["Breakdown by Party", "Paid Out", "Received"].map(csvCell).join(","));
        summary.byParty.forEach(([p, v]) => lines.push([p, v.payout, v.payment].map(csvCell).join(",")));
        lines.push("");
        lines.push([`Unreconciled entries (${summary.unreconciled.length})`].map(csvCell).join(","));
        lines.push(["Date", "Party", "Type", "Mode", "Amount"].map(csvCell).join(","));
        summary.unreconciled.forEach((r) => lines.push([r.date, r.party_name, r.txn_type, r.payment_mode, r.amount].map(csvCell).join(",")));
        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `reconcile_${data?.date_from || "all"}_${data?.date_to || "all"}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const submitMark = async () => {
        if (!markRow) return;
        setMarking(true);
        try {
            await api.post(`/reports/payment-calendar/${markRow.id}/reconcile`, { reconciled: true, note: markRow.note || "" });
            toast.success("Marked as reconciled");
            setMarkRow(null);
            await load();
        } catch (e) {
            toast.error("Failed to mark reconciled");
        } finally {
            setMarking(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="payment-calendar-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Reports</div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-slate-900">Payment Calendar</h1>
                    <p className="mt-2 text-sm text-slate-500">
                        {kindLabel} — grouped date-wise. Filter by date range or a single party.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        data-testid="payment-calendar-reconcile"
                        variant="outline"
                        onClick={() => setReconcileOpen(true)}
                        disabled={!data}
                        className="border-amber-200 text-amber-700 hover:bg-amber-50"
                    >
                        <Scales size={14} className="mr-1.5" /> Reconcile
                    </Button>
                    <Button
                        data-testid="payment-calendar-refresh"
                        variant="outline"
                        onClick={load}
                        disabled={busy}
                        className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    >
                        <ArrowClockwise size={14} className="mr-1.5" /> {busy ? "Loading…" : "Refresh"}
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Kind</Label>
                        <Select value={kind} onValueChange={setKind}>
                            <SelectTrigger data-testid="pc-kind" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="payout">Payments Made (to parties)</SelectItem>
                                <SelectItem value="payment">Payments Received (from parties)</SelectItem>
                                <SelectItem value="both">Both</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Mode</Label>
                        <Select value={mode} onValueChange={setMode}>
                            <SelectTrigger data-testid="pc-mode" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Modes</SelectItem>
                                {availableModes.map((m) => (
                                    <SelectItem key={m} value={m}>{m}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">From</Label>
                        <Input data-testid="pc-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">To</Label>
                        <Input data-testid="pc-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1" />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Party</Label>
                        <Select value={partyId} onValueChange={setPartyId}>
                            <SelectTrigger data-testid="pc-party" className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All parties</SelectItem>
                                {parties.filter((p) => p.active !== false).map((p) => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <Button data-testid="pc-apply" onClick={load} disabled={busy} className="bg-blue-700 text-white hover:bg-blue-800">
                        {busy ? "Loading…" : "Apply filters"}
                    </Button>
                </div>
            </div>

            {/* KPI cards */}
            {data && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-4">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-violet-800">
                            <PaperPlaneTilt size={13} /> Total Paid Out
                        </div>
                        <div data-testid="pc-total-payout" className="mt-1 font-display text-2xl font-bold text-violet-900 tabular-nums">{fmt(data.total_payout)}</div>
                        <div className="mt-0.5 text-[11px] text-violet-700/70">to parties in this window</div>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-emerald-800">
                            <CurrencyInr size={13} /> Total Received
                        </div>
                        <div data-testid="pc-total-payment" className="mt-1 font-display text-2xl font-bold text-emerald-900 tabular-nums">{fmt(data.total_payment)}</div>
                        <div className="mt-0.5 text-[11px] text-emerald-700/70">from parties in this window</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-4">
                        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-600">
                            <CalendarBlank size={13} /> Days with activity
                        </div>
                        <div className="mt-1 font-display text-2xl font-bold text-slate-900 tabular-nums">{groups.length}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{data.count} transaction{data.count === 1 ? "" : "s"} total</div>
                    </div>
                </div>
            )}

            {/* Groups */}
            {!busy && groups.length === 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-10 text-center" data-testid="pc-empty">
                    <CalendarBlank size={32} weight="duotone" className="mx-auto text-slate-300" />
                    <div className="mt-3 font-display text-lg font-semibold text-slate-700">No payments in this window</div>
                    <div className="mt-1 text-sm text-slate-500">Try widening the date range or switching the Kind / Mode filter.</div>
                </div>
            )}

            <div className="space-y-4">
                {groups.map((g) => (
                    <div key={g.date} data-testid={`pc-group-${g.date}`} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                        <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-slate-100 bg-blue-50/50 px-4 py-3">
                            <div className="flex items-center gap-2">
                                <CalendarBlank size={16} weight="duotone" className="text-blue-700" />
                                <div className="font-display text-base font-bold text-slate-900">{fmtDate(g.date)}</div>
                                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-600 ring-1 ring-slate-200">
                                    {g.count} entr{g.count === 1 ? "y" : "ies"}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs">
                                {g.payout_total > 0 && (
                                    <span className="font-semibold text-violet-800">
                                        Paid out: <span className="tabular-nums">{fmt(g.payout_total)}</span>
                                    </span>
                                )}
                                {g.payment_total > 0 && (
                                    <span className="font-semibold text-emerald-800">
                                        Received: <span className="tabular-nums">{fmt(g.payment_total)}</span>
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Party</th>
                                        <th className="px-4 py-2 text-left">Type</th>
                                        <th className="px-4 py-2 text-left">Reference / UTR</th>
                                        <th className="px-4 py-2 text-left">Mode</th>
                                        <th className="px-4 py-2 text-left">Status</th>
                                        <th className="px-4 py-2 text-left">Description</th>
                                        <th className="px-4 py-2 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {g.rows.map((r) => (
                                        <tr key={r.id} data-testid={`pc-row-${r.id}`} data-backdated={r.backdated ? "1" : "0"} data-suspense={r.is_suspense ? "1" : "0"} className={r.is_suspense ? "bg-orange-100 hover:bg-orange-200" : r.backdated ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-slate-50/60"}>
                                            <td className="px-4 py-2">
                                                <Link
                                                    to={`/admin/reports?party_id=${r.party_id}`}
                                                    className="font-medium text-blue-700 hover:underline"
                                                >
                                                    {r.party_name}
                                                </Link>
                                            </td>
                                            <td className="px-4 py-2">
                                                {r.txn_type === "payout" ? (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-800">
                                                        <PaperPlaneTilt size={10} weight="fill" /> Paid out
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-800">
                                                        <CurrencyInr size={10} weight="fill" /> Received
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 font-mono text-xs text-slate-700">
                                                <div className="flex flex-col gap-0.5">
                                                    <span>{r.reference || "—"}</span>
                                                    {r.backdated && (
                                                        <span
                                                            className="inline-flex w-fit items-center gap-1 rounded-full bg-amber-400 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-950"
                                                            title={`Transaction date ${dmy(r.txn_date)} · Entered ${dmy(r.entry_date)}`}
                                                            data-testid={`pc-backdated-${r.id}`}
                                                        >
                                                            Backdated · Entered {dmy(r.entry_date)}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 text-xs text-slate-600">
                                                {r.payment_mode || "—"}
                                                {r.is_suspense && <span data-testid={`pc-suspense-${r.id}`} className="ml-1.5 inline-flex items-center rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">SUSPENSE</span>}
                                            </td>
                                            <td className="px-4 py-2">
                                                {r.reconciled ? (
                                                    <span
                                                        data-testid={`pc-badge-${r.id}`}
                                                        title={r.reconcile_note ? `Reconciled · ${r.reconcile_note}` : "Reconciled"}
                                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-200"
                                                    >
                                                        <CheckCircle size={11} weight="fill" /> Reconciled
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        data-testid={`pc-badge-${r.id}`}
                                                        onClick={() => setMarkRow({ id: r.id, note: "" })}
                                                        title="Click to mark as reconciled"
                                                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 ring-1 ring-amber-200 transition hover:bg-amber-100"
                                                    >
                                                        <WarningCircle size={11} weight="fill" /> Unreconciled
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-xs text-slate-600" title={r.description}>
                                                <div className="max-w-[320px] truncate">{r.description || "—"}</div>
                                            </td>
                                            <td className={`px-4 py-2 text-right font-semibold tabular-nums ${r.txn_type === "payout" ? "text-violet-800" : "text-emerald-800"}`}>
                                                {fmt(r.amount)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}
            </div>

            {/* Reconcile summary dialog */}
            <Dialog open={reconcileOpen} onOpenChange={setReconcileOpen}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pc-reconcile-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Scales size={18} className="text-amber-600" /> Reconciliation Summary
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5">
                        <div className="text-xs text-slate-500">
                            Period: <span className="font-semibold text-slate-700">{data?.date_from || "—"}</span> to <span className="font-semibold text-slate-700">{data?.date_to || "—"}</span>
                            {mode !== "all" && <> · Mode: <span className="font-semibold text-slate-700">{mode}</span></>}
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-violet-800">Total Paid Out</div>
                                <div data-testid="pc-rec-paidout" className="mt-1 text-lg font-bold text-violet-900 tabular-nums">{fmt(summary.paidOut)}</div>
                            </div>
                            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-800">Total Received</div>
                                <div data-testid="pc-rec-received" className="mt-1 text-lg font-bold text-emerald-900 tabular-nums">{fmt(summary.received)}</div>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">Net Position</div>
                                <div data-testid="pc-rec-net" className={`mt-1 text-lg font-bold tabular-nums ${summary.net < 0 ? "text-rose-700" : "text-slate-900"}`}>{fmt(summary.net)}</div>
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Breakdown by Mode</div>
                            <div className="rounded-lg border border-slate-200">
                                {summary.byMode.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No data</div>}
                                {summary.byMode.map(([m, amt]) => (
                                    <div key={m} className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-sm last:border-0">
                                        <span className="text-slate-700">{m}</span>
                                        <span className="font-semibold tabular-nums text-slate-900">{fmt(amt)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Breakdown by Party</div>
                            <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200">
                                {summary.byParty.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No data</div>}
                                {summary.byParty.map(([p, v]) => (
                                    <div key={p} className="flex items-center justify-between border-b border-slate-100 px-3 py-1.5 text-sm last:border-0">
                                        <span className="text-slate-700">{p}</span>
                                        <span className="flex gap-3 text-xs tabular-nums">
                                            {v.payout > 0 && <span className="text-violet-700">Out {fmt(v.payout)}</span>}
                                            {v.payment > 0 && <span className="text-emerald-700">In {fmt(v.payment)}</span>}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div>
                            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500">
                                Unreconciled Entries ({summary.unreconciled.length}) — missing UTR / Reference
                            </div>
                            <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50/40">
                                {summary.unreconciled.length === 0 && <div className="px-3 py-2 text-xs text-emerald-600">All entries reconciled ✓</div>}
                                {summary.unreconciled.map((r) => (
                                    <div key={r.id} className="flex items-center justify-between border-b border-amber-100 px-3 py-1.5 text-xs last:border-0">
                                        <span className="text-slate-700">{fmtDate(r.date)} · {r.party_name} · {r.payment_mode || "no mode"}</span>
                                        <span className="font-semibold tabular-nums text-slate-900">{fmt(r.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button data-testid="pc-rec-export" variant="outline" onClick={exportCsv} className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                            <DownloadSimple size={14} className="mr-1.5" /> Export CSV
                        </Button>
                        <Button onClick={() => setReconcileOpen(false)} className="bg-slate-800 text-white hover:bg-slate-900">Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Mark reconciled dialog */}
            <Dialog open={!!markRow} onOpenChange={(o) => { if (!o) setMarkRow(null); }}>
                <DialogContent className="max-w-md" data-testid="pc-mark-dialog">
                    <DialogHeader>
                        <DialogTitle>Mark as Reconciled</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-slate-500">Note (optional)</Label>
                        <Textarea
                            data-testid="pc-mark-note"
                            value={markRow?.note || ""}
                            onChange={(e) => setMarkRow((s) => s ? { ...s, note: e.target.value } : s)}
                            placeholder="e.g. Verified against bank statement / confirmed on WhatsApp"
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMarkRow(null)} disabled={marking}>Cancel</Button>
                        <Button data-testid="pc-mark-submit" onClick={submitMark} disabled={marking} className="bg-emerald-700 text-white hover:bg-emerald-800">
                            {marking ? "Saving…" : "Mark Reconciled"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
